/**
 * ErrorMessage component
 * Displays error messages with optional retry action
 */

import { useTranslation } from 'react-i18next';
import { Button } from './Button';

interface ErrorMessageProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  fullScreen?: boolean;
}

/**
 * Error message component with retry functionality
 *
 * @example
 * ```tsx
 * <ErrorMessage
 *   title="Failed to load"
 *   message="Could not fetch data"
 *   onRetry={refetch}
 * />
 * ```
 */
export function ErrorMessage({
  title,
  message,
  onRetry,
  fullScreen = false,
}: ErrorMessageProps) {
  const { t } = useTranslation();
  const errorTitle = title || t('errors.error');

  const content = (
    <div className="rounded-lg bg-error-50 border border-error-200 p-4" role="alert">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-error-600"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-error-800">{errorTitle}</h3>
          <div className="mt-1 text-sm text-error-700">{message}</div>
          {onRetry && (
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="border-error-600 text-error-600 hover:bg-error-50"
              >
                {t('errors.tryAgain')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full">{content}</div>
      </div>
    );
  }

  return content;
}
